import React, { useEffect, useState } from "react"
import { Badge, Box, Button, Modal, ModalBody, ModalContent, ModalFooter, ModalHeader, ModalOverlay, Text } from "@chakra-ui/react"
import { getDoctorById, verifyActivateDoctor, deactivateDoctor } from "../../Http/Index"
import { ShowAlert } from "../../GlobalComponent/Alerts/Alert"
import { RegistryDoctor, DoctorSelectorBanner } from "./shared.types"

interface Props {
  doctors: RegistryDoctor[]
  selected: RegistryDoctor | null
  onSelectDoctor: (doc: RegistryDoctor) => void
  onGoToRegistry: () => void
  onRefreshDoctors: () => void
  refreshingDoctors: boolean
}

export const Verification: React.FC<Props> = ({ doctors, selected, onSelectDoctor, onGoToRegistry, onRefreshDoctors, refreshingDoctors }) => {
  const [activating, setActivating] = useState(false)
  const [doctorDetail, setDoctorDetail] = useState<any>(null)
  const [loadingDetail, setLoadingDetail] = useState(false)
  const [pendingAction, setPendingAction] = useState<"activate" | "deactivate" | null>(null)

  const loadDetail = async (id: string) => {
    try { setLoadingDetail(true); const { data } = await getDoctorById(id); setDoctorDetail(data?.data ?? data) }
    catch { }
    finally { setLoadingDetail(false) }
  }

  useEffect(() => {
    setDoctorDetail(null)
    if (!selected) return
    loadDetail(selected._id)
  }, [selected?._id]) // eslint-disable-line react-hooks/exhaustive-deps

  const d = doctorDetail ?? selected
  const docs: any[] = doctorDetail?.documents ?? []
  const verifiedDocs = docs.filter((doc: any) => doc.verified)
  const phone = d?.phone ?? (d?.mobileNo ? `+${d.mobileNo.countryCode}${d.mobileNo.number}` : "—")
  const regNumber = d?.registration_number ?? d?.registrationNumber ?? "—"
  const address = [d?.address_line1, d?.city, d?.state, d?.country, d?.pincode].filter(Boolean).join(", ")

  const checklist = [
    { label: "Profile Created",    desc: "Basic details established.",                      done: !!d },
    { label: "Documents Uploaded", desc: "Required credentials received.",                  done: docs.length > 0 },
    { label: "Documents Verified", desc: "Primary Source Verification completed by admin.", done: docs.length > 0 && verifiedDocs.length === docs.length },
  ]

  return (
    <Box display="grid" gridTemplateColumns={{ base: "1fr", md: "8fr 4fr" }} gap={4} mt={4}>
      <Box gridColumn="1 / -1">
        <DoctorSelectorBanner doctors={doctors} selected={selected} onSelect={onSelectDoctor} onGoToRegistry={onGoToRegistry}
          onRefresh={async () => { onRefreshDoctors(); if (selected) await loadDetail(selected._id) }}
          refreshing={refreshingDoctors || loadingDetail} />
      </Box>

      {!selected ? (
        <Box gridColumn="1 / -1" p={8} border="1px dashed" borderColor="gray.200" borderRadius="md" textAlign="center">
          <Text fontSize="lg" mb={2}>✅</Text>
          <Text fontWeight="semibold" mb={1}>No Doctor Selected</Text>
          <Text fontSize="sm" color="gray.500">Select a doctor above or go to Registry and click ✅ Verify.</Text>
        </Box>
      ) : (
        <React.Fragment>
          {/* Left column */}
          <Box display="flex" flexDir="column" gap={4}>
            <Box display="flex" alignItems="center" gap={3}>
              <Box px={3} py={1} borderRadius="full" border="1px solid"
                borderColor={d?.status === "active" ? "green.400" : "red.300"}
                bg={d?.status === "active" ? "green.500" : "red.50"}
                display="flex" alignItems="center" gap={2}>
                <Box w={2} h={2} borderRadius="full" bg={d?.status === "active" ? "white" : "red.400"} />
                <Text fontSize="xs" fontWeight="semibold" color={d?.status === "active" ? "white" : "red.700"}>
                  Status: {d?.status ?? "Pending Review"}
                </Text>
              </Box>
              {loadingDetail && <Text fontSize="xs" color="gray.400">Loading...</Text>}
            </Box>

            {/* Profile Card */}
            <Box border="1px solid" borderColor="gray.200" borderRadius="lg" p={6} bg="white" shadow="sm">
              <Text fontWeight="bold" fontSize="md" mb={4}>👤 Applicant Profile</Text>
              <Box display="grid" gridTemplateColumns="1fr 1fr" gap={4}>
                {[
                  { label: "Full Name", value: d?.name }, { label: "Specialization", value: d?.specialization },
                  { label: "Email", value: d?.email }, { label: "Phone", value: phone },
                  { label: "Qualification", value: d?.qualification }, { label: "Doctor Type", value: d?.doctor_type ?? "—" },
                  { label: "Reg. Number", value: regNumber }, { label: "Reg. Council", value: d?.registration_council ?? "—" },
                  { label: "Years Exp.", value: d?.years_of_experience != null ? `${d.years_of_experience} yrs` : "—" },
                  { label: "Organization", value: d?.organization_name ?? "—" },
                ].map((f) => (
                  <Box key={f.label}>
                    <Text fontSize="xs" fontWeight="semibold" color="gray.500" mb={1}>{f.label}</Text>
                    <Text fontSize="sm">{f.value || "—"}</Text>
                  </Box>
                ))}
                {address && <Box gridColumn="1 / -1"><Text fontSize="xs" fontWeight="semibold" color="gray.500" mb={1}>Address</Text><Text fontSize="sm">{address}</Text></Box>}
              </Box>
            </Box>

            {/* Documents Table */}
            <Box border="1px solid" borderColor="gray.200" borderRadius="lg" bg="white" shadow="sm" overflow="hidden">
              <Box px={4} py={3} borderBottom="1px solid" borderColor="gray.100" bg="gray.50" display="flex" justifyContent="space-between" alignItems="center">
                <Text fontWeight="semibold" fontSize="sm">📁 Documents</Text>
                <Badge colorScheme={verifiedDocs.length === docs.length && docs.length > 0 ? "green" : "orange"} fontSize="xs">{verifiedDocs.length}/{docs.length} Verified</Badge>
              </Box>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ background: "#f9f9f9", borderBottom: "1px solid #e2e8f0" }}>
                    {["Document Type", "File", "Uploaded", "Status"].map((h) => (
                      <th key={h} style={{ padding: "10px 14px", textAlign: "left" }}>
                        <Text fontSize="xs" color="gray.500" fontWeight="semibold">{h}</Text>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {docs.length === 0 ? (
                    <tr><td colSpan={4} style={{ padding: "20px", textAlign: "center" }}><Text fontSize="sm" color="gray.400">No documents uploaded yet.</Text></td></tr>
                  ) : docs.map((doc: any) => (
                    <tr key={doc._id} style={{ borderBottom: "1px solid #edf2f7" }}>
                      <td style={{ padding: "10px 14px" }}><Text fontSize="sm" fontWeight="semibold">{doc.document_type}</Text></td>
                      <td style={{ padding: "10px 14px" }}><Text fontSize="sm" color="gray.600">{doc.document_url?.split("/").pop() ?? "—"}</Text></td>
                      <td style={{ padding: "10px 14px" }}><Text fontSize="sm" color="gray.500">{doc.createdAt ? new Date(doc.createdAt).toLocaleDateString("en-IN") : "—"}</Text></td>
                      <td style={{ padding: "10px 14px" }}><Badge colorScheme={doc.verified ? "green" : "orange"} fontSize="xs">{doc.verified ? "✓ Verified" : "Pending"}</Badge></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Box>
          </Box>

          {/* Right column */}
          <Box display="flex" flexDir="column" gap={4}>
            {/* Checklist */}
            <Box border="1px solid" borderColor="gray.200" borderRadius="lg" p={6} bg="white" shadow="sm">
              <Text fontWeight="bold" fontSize="sm" mb={6}>✅ Verification Checklist</Text>
              <Box display="flex" flexDir="column" gap={0}>
                {checklist.map((step, i) => (
                  <Box key={step.label} display="flex" gap={4} position="relative" pb={i < checklist.length - 1 ? 6 : 0}>
                    {i < checklist.length - 1 && (
                      <Box position="absolute" left="11px" top="24px" bottom={0} w="2px" bg={step.done ? "blue.500" : "gray.200"} />
                    )}
                    <Box w={6} h={6} borderRadius="full" flexShrink={0} bg={step.done ? "blue.600" : "gray.200"}
                      display="flex" alignItems="center" justifyContent="center" zIndex={1} border="2px solid white">
                      <Text fontSize="xs" color="white" fontWeight="bold">{step.done ? "✓" : i + 1}</Text>
                    </Box>
                    <Box pt="1px">
                      <Text fontWeight="semibold" fontSize="sm" mb={1}>{step.label}</Text>
                      <Text fontSize="xs" color="gray.500">{step.desc}</Text>
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>

            {/* Final Decision */}
            <Box border="1px solid" borderColor="gray.200" borderTop="4px solid" style={{ borderTopColor: "#003d9b" }} borderRadius="lg" p={6} bg="white" shadow="sm">
              <Text fontWeight="bold" fontSize="md" mb={1}>Final Decision</Text>
              <Text fontSize="xs" color="gray.500" mb={4}>Activating confirms this individual is authorized to practice within network parameters.</Text>
              {d?.status === "active" ? (
                <Button w="full" colorScheme="red" variant="outline" isLoading={activating} onClick={() => setPendingAction("deactivate")}>Deactivate Doctor</Button>
              ) : (
                <Button w="full" colorScheme="blue" isLoading={activating}
                  isDisabled={!checklist.every((s) => s.done) || activating}
                  onClick={() => setPendingAction("activate")}>
                  Approve & Activate Doctor
                </Button>
              )}
            </Box>

            <Modal isOpen={!!pendingAction} onClose={() => setPendingAction(null)} isCentered size="sm">
              <ModalOverlay />
              <ModalContent>
                <ModalHeader fontSize="md">{pendingAction === "activate" ? "Activate Doctor" : "Deactivate Doctor"}</ModalHeader>
                <ModalBody>
                  <Text fontSize="sm">
                    Are you sure you want to <Text as="span" fontWeight="semibold">{pendingAction === "activate" ? "approve and activate" : "deactivate"}</Text>{" "}
                    <Text as="span" fontWeight="semibold">{selected?.name}</Text>?
                  </Text>
                </ModalBody>
                <ModalFooter gap={2}>
                  <Button size="sm" variant="ghost" onClick={() => setPendingAction(null)}>No</Button>
                  <Button size="sm" colorScheme={pendingAction === "activate" ? "blue" : "red"} isLoading={activating}
                    onClick={async () => {
                      if (!selected) return
                      try {
                        setActivating(true)
                        if (pendingAction === "activate") { await verifyActivateDoctor(selected._id); ShowAlert("success", `${selected.name} has been activated`) }
                        else { await deactivateDoctor(selected._id); ShowAlert("success", `${selected.name} has been deactivated`) }
                        setPendingAction(null)
                        await loadDetail(selected._id)
                      } catch (e: any) { ShowAlert("error", e?.response?.data?.message || "Action failed") }
                      finally { setActivating(false) }
                    }}>Yes</Button>
                </ModalFooter>
              </ModalContent>
            </Modal>
          </Box>
        </React.Fragment>
      )}
    </Box>
  )
}
